import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, X, ArrowLeft } from 'lucide-react';
import logo from '../assets/logo.jpg';
import toast from 'react-hot-toast';

// Mock subscription service
const mockSubs = {
  startCheckout: async (priceId: string) => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    console.log(`Starting checkout for price ID: ${priceId}`);
    // In real implementation, this would redirect to Stripe/payment processor
    return { success: true, checkoutUrl: '/checkout' };
  }
};

const PricingPage: React.FC = () => {
  const [isAnnual, setIsAnnual] = useState(false);
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleProCheckout = async () => {
    const priceId = isAnnual ? 'price_annual_pro' : 'price_monthly_pro';
    setIsLoading('pro');

    try {
      await mockSubs.startCheckout(priceId);
      toast.success('Redirecting to checkout...');
      // In real implementation, user would be redirected to payment processor
      setTimeout(() => {
        navigate('/dashboard');
        toast.success('Welcome to Fibre Pro!');
      }, 2000);
    } catch (error) {
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setIsLoading(null);
    }
  };

  const handleTrialContinue = () => {
    navigate('/dashboard');
    toast.success('Continuing with Trial plan');
  };

  const plans = {
    trial: {
      name: 'Trial',
      description: 'Perfect for getting started',
      monthlyPrice: 0,
      annualPrice: 0,
      features: [
        { name: 'Up to 3 investigation boards', included: true },
        { name: 'Basic entity types (Person, Location, etc.)', included: true },
        { name: 'Simple connections and notes', included: true },
        { name: 'Limited storage (100MB)', included: true },
        { name: 'Basic export (JSON only)', included: true },
        { name: 'Unlimited boards', included: false },
        { name: 'File attachments', included: false },
        { name: 'Advanced export (PNG, PDF)', included: false },
        { name: 'Timeline view', included: false },
        { name: 'Collaboration features', included: false },
        { name: 'Priority support', included: false }
      ]
    },
    pro: {
      name: 'Fibre Pro',
      description: 'For serious investigations',
      monthlyPrice: 12,
      annualPrice: 120, // $10/month when billed annually
      features: [
        { name: 'Unlimited investigation boards', included: true },
        { name: 'All entity types and custom fields', included: true },
        { name: 'Advanced connections and relationships', included: true },
        { name: 'Unlimited storage', included: true },
        { name: 'File attachments (images, documents, videos)', included: true },
        { name: 'Advanced export (JSON, PNG, PDF)', included: true },
        { name: 'Interactive timeline view', included: true },
        { name: 'Real-time collaboration', included: true },
        { name: 'Advanced search and filtering', included: true },
        { name: 'Priority email support', included: true },
        { name: 'API access', included: true }
      ]
    }
  };

  const currentTrialPrice = isAnnual ? plans.trial.annualPrice : plans.trial.monthlyPrice;
  const currentProPrice = isAnnual ? plans.pro.annualPrice : plans.pro.monthlyPrice;
  const proMonthlyEquivalent = isAnnual ? (plans.pro.annualPrice / 12).toFixed(2) : plans.pro.monthlyPrice;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft size={16} />
            Back to Dashboard
          </Link>

          <div className="flex justify-center mb-6">
            <div className="flex items-center gap-3">
              <img src={logo} alt="Fibre" className="h-8 w-auto" />
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Fibre</h1>
            </div>
          </div>

          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Choose Your Plan
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Start with our free trial or unlock the full power of Fibre Pro for advanced investigations
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-12">
          <div className="bg-white dark:bg-gray-800 p-1 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <button
                onClick={() => setIsAnnual(false)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${!isAnnual
                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                  : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                  }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setIsAnnual(true)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 relative ${isAnnual
                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                  : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                  }`}
              >
                Annual
                <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  Save 17%
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Trial Plan */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-8 relative">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {plans.trial.name}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {plans.trial.description}
              </p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900 dark:text-white">
                  ${currentTrialPrice}
                </span>
                <span className="text-gray-600 dark:text-gray-400 ml-2">
                  {isAnnual ? '/year' : '/month'}
                </span>
              </div>
              <button
                onClick={handleTrialContinue}
                className="w-full py-3 px-6 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-900 dark:hover:text-white transition-all duration-200"
              >
                Stay on Trial
              </button>
            </div>

            {/* Features */}
            <div className="space-y-4">
              {plans.trial.features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  {feature.included ? (
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                  ) : (
                    <X className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                  <span className={`text-sm ${feature.included
                    ? 'text-gray-900 dark:text-white'
                    : 'text-gray-500 dark:text-gray-400'
                    }`}>
                    {feature.name}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Pro Plan */}
          <div className="bg-slate-900 rounded-2xl shadow-2xl border border-tech-blue p-8 relative">
            {/* Popular Badge */}
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span className="bg-tech-blue text-white px-4 py-1 rounded-full text-sm font-medium shadow-[0_0_15px_rgba(59,130,246,0.6)]">
                Powered by Fibre
              </span>
            </div>

            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-white mb-2 font-mono">
                {plans.pro.name}
              </h3>
              <p className="text-gray-400 mb-6 font-mono text-sm">
                Full Anduril-grade capabilities
              </p>
              <div className="mb-2">
                <span className="text-4xl font-bold text-white">
                  $4.99
                </span>
                <span className="text-gray-400 ml-2">
                  /month
                </span>
              </div>
              <p className="text-sm text-green-400 mb-6">
                7-day free trial, cancel anytime
              </p>

              <button
                onClick={handleProCheckout}
                disabled={isLoading === 'pro'}
                className="w-full py-3 px-6 bg-tech-blue text-white font-medium rounded-lg hover:bg-blue-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_20px_rgba(59,130,246,0.4)]"
              >
                {isLoading === 'pro' ? 'Processing...' : 'Start 7-Day Trial'}
              </button>
            </div>

            {/* Features */}
            <div className="space-y-4">
              {plans.pro.features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-tech-cyan flex-shrink-0" />
                  <span className="text-sm text-gray-300 font-mono">
                    {feature.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* FAQ or Additional Info */}
        <div className="text-center mt-16">
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Need help choosing? We're here to help.
          </p>
          <div className="flex justify-center gap-6 text-sm">
            <a href="mailto:support@fibre.com" className="text-blue-600 dark:text-blue-400 hover:underline">
              Contact Support
            </a>
            <a href="/docs" className="text-blue-600 dark:text-blue-400 hover:underline">
              View Documentation
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
